import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Popover, Tooltip, Chip } from '@mui/material';
import { EmojiEmotions, LocalOffer } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSmsSegmentInfo } from 'utils/smsEstimatedCreditCost';

const SMS_QUICK_EMOJIS: string[] = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🙂', '😉', '😊', '😍', '🤩', '😘', '😋', '😛', '🤔', '😏', '😌', '😢', '😭', '😤', '😡', '🤗',
  '🤝', '👍', '👎', '👏', '🙏', '👋', '✌️', '🙌', '💪', '👆', '👇', '👉', '👈',
  '❤️', '💛', '💚', '💙', '💜', '🖤', '💔', '❣️', '💯', '🔥', '✨', '⭐', '🌟',
  '✅', '❌', '⚠️', '📌', '📍', '🔔', '📧', '📱', '💬', '🎉', '🎁', '✈️', '🏠',
  '⏰', '📅', '💰', '🔒', '🔑', '☀️', '🌙', '☁️', '🌈', '❄️', '☕', '🍕', '🎂',
];

function escapeHtmlForSmsQuillPlain(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function plainTextToQuillHtml(plain: string): string {
  if (!plain) return '<p><br></p>';
  return plain
    .split('\n')
    .map((line) => `<p>${line ? escapeHtmlForSmsQuillPlain(line) : '<br>'}</p>`)
    .join('');
}

const SMS_QUILL_MODULES = { toolbar: false };

export type SmsContentFieldProps = {
  dialogOpen: boolean;
  /** Resets editor when switching records (e.g. template _id) */
  contentKey?: string;
  value: string;
  onChange: (plain: string) => void;
  readOnly?: boolean;
  /** Optional override for the purple helper box below the editor */
  helperText?: React.ReactNode;
};

const DEFAULT_HELPER = (
  <>
    You can use DataSet column variables above, or numbered placeholders {'{{1}}'}, {'{{2}}'}, etc. Only plain text is sent as SMS;
    use the emoji button for symbols. Non–GSM-7 characters use <strong>UCS-2</strong> (segment count updates live).
  </>
);

/**
 * SMS template body: Quill (plain text via getText), emoji strip, live segment chip — matches Instant Updates + System Templates SMS UX.
 */
export const SmsContentField: React.FC<SmsContentFieldProps> = ({
  dialogOpen,
  contentKey,
  value,
  onChange,
  readOnly = false,
  helperText = DEFAULT_HELPER,
}) => {
  const initialPlain = String(value ?? '');
  const [textValue, setTextValue] = useState<string>(initialPlain);
  const [editorHtml, setEditorHtml] = useState<string>(() => plainTextToQuillHtml(initialPlain));
  const quillRef = useRef<ReactQuill | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!dialogOpen) return;
    const plain = String(value ?? '');
    setTextValue(plain);
    setEditorHtml(plainTextToQuillHtml(plain));
  }, [dialogOpen, contentKey]);

  const smsSegmentInfo = useMemo(() => getSmsSegmentInfo(textValue), [textValue]);

  const handleSmsQuillChange = (
    html: string,
    _delta: unknown,
    _source: string,
    editor: { getText: () => string }
  ) => {
    if (readOnly) return;
    setEditorHtml(html);
    let plain = editor.getText();
    if (plain.endsWith('\n')) plain = plain.slice(0, -1);
    setTextValue(plain);
    onChange(plain);
  };

  const insertSmsEmoji = (emoji: string) => {
    if (readOnly) return;
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      const range = quill.getSelection(true);
      let index = Math.max(0, quill.getLength() - 1);
      if (range && range.index != null) index = range.index;
      quill.insertText(index, emoji, 'user');
      const nextLen = emoji.length;
      quill.setSelection(index + nextLen, 0, 'silent');
    } else {
      const next = textValue + emoji;
      setTextValue(next);
      setEditorHtml(plainTextToQuillHtml(next));
      onChange(next);
    }
    setEmojiAnchorEl(null);
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Template Content *
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          '& .ql-container.ql-snow': {
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'divider',
            fontSize: '0.95rem',
            minHeight: 140,
          },
          '& .ql-editor': {
            minHeight: 120,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            px: 0.5,
            py: 0.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
          }}
        >
          {!readOnly ? (
            <Tooltip title="Insert emoji (uses UCS-2 encoding for the SMS)">
              <IconButton
                size="small"
                color="secondary"
                aria-label="Insert emoji"
                onClick={(e) => setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget)}
              >
                <EmojiEmotions fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ width: 8 }} />
          )}
          <Chip
            icon={<LocalOffer sx={{ fontSize: 18 }} />}
            label={`${smsSegmentInfo.segments} SMS segment${smsSegmentInfo.segments === 1 ? '' : 's'} (${smsSegmentInfo.encoding})`}
            size="small"
            sx={{
              ml: 'auto',
              fontWeight: 800,
              color: 'success.main',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.96)'),
              border: '1px solid',
              borderColor: 'success.main',
              height: 28,
              maxWidth: { xs: '100%', sm: 'min(100%, 340px)' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              '& .MuiChip-icon': {
                ml: 0.5,
                color: 'success.main',
              },
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '0.75rem',
                whiteSpace: 'normal',
                textAlign: 'right',
                lineHeight: 1.25,
              },
            }}
          />
        </Box>
        {!readOnly && (
          <Popover
            open={Boolean(emojiAnchorEl)}
            anchorEl={emojiAnchorEl}
            onClose={() => setEmojiAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 1, maxWidth: 320, maxHeight: 280, overflow: 'auto' } } }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-start' }}>
              {SMS_QUICK_EMOJIS.map((emoji, idx) => (
                <IconButton
                  key={`sms-emoji-${idx}`}
                  size="small"
                  onClick={() => insertSmsEmoji(emoji)}
                  sx={{
                    fontSize: '1.35rem',
                    width: 36,
                    height: 36,
                    p: 0,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  aria-label={`Insert ${emoji}`}
                >
                  <span style={{ lineHeight: 1 }}>{emoji}</span>
                </IconButton>
              ))}
            </Box>
          </Popover>
        )}
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorHtml}
          onChange={handleSmsQuillChange}
          modules={SMS_QUILL_MODULES}
          formats={[]}
          readOnly={readOnly}
        />
      </Box>
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)'}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" component="div">
          {helperText}
        </Typography>
      </Box>
    </Box>
  );
};
